<script setup lang="ts">
import { mdiGithub, mdiOpenInNew } from '@mdi/js'

const props = defineProps<{
  modelValue: boolean
  repoFullName: string
  repoName: string
}>()

const emit = defineEmits(['update:modelValue'])

const api = useApi()
const loading = ref(true)
const readmeContent = ref<string | null>(null)
const error = ref<string | null>(null)

const visible = computed({
  get: () => props.modelValue,
  set: (v: boolean) => emit('update:modelValue', v),
})

watch(() => props.modelValue, async (open) => {
  if (!open) return
  loading.value = true
  error.value = null
  readmeContent.value = null
  try {
    const parts = props.repoFullName.split('/')
    const owner = parts[0] ?? ''
    const repo = parts[1] ?? ''
    const res = await api.getGithubRepoReadme(owner, repo)
    readmeContent.value = res.readme
  }
  catch {
    error.value = 'README の取得に失敗しました'
  }
  finally {
    loading.value = false
  }
})
</script>

<template>
  <v-dialog
    v-model="visible"
    max-width="600"
  >
    <v-card class="readme-card">
      <v-card-title class="readme-title">
        <v-icon :icon="mdiGithub" />
        {{ repoName }}
        <v-spacer />
        <v-btn
          variant="text"
          size="small"
          :append-icon="mdiOpenInNew"
          :href="`https://github.com/${repoFullName}`"
          target="_blank"
          rel="noopener noreferrer"
        >
          GitHub で見る
        </v-btn>
      </v-card-title>

      <v-card-text class="readme-body">
        <div
          v-if="loading"
          class="text-center pa-4"
        >
          <v-progress-circular
            indeterminate
            color="primary"
          />
        </div>

        <div
          v-else-if="error"
          class="text-center pa-4 text-error"
        >
          {{ error }}
        </div>

        <div
          v-else-if="!readmeContent"
          class="text-center pa-4 text-grey"
        >
          README が見つかりません
        </div>

        <MDC
          v-else
          :value="readmeContent"
        />
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          variant="text"
          @click="visible = false"
        >
          閉じる
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.readme-card {
  font-family: "Noto Sans JP", sans-serif;
}

.readme-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.readme-body {
  max-height: 60vh;
  overflow-y: auto;
}
</style>
